import React, { useMemo, useState } from 'react';
import { Alert, Grid, TextField, Typography } from '@mui/material';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { LoadingButton } from '@mui/lab';
import { GrowconnectService, PaymentIntegrationType } from '../../api';
import {
  classifyRegisterError,
  normalizeBusinessNumber,
  normalizeIsraeliMobile,
  RegisterError,
  RegisterErrorType,
} from '../growConnectUtils';

type GrowConnectFormValues = {
  business_number: string;
  phone: string;
};

type GrowConnectFormProps = {
  /** Rendered inside the pending state, to send the form link to a corrected mobile. */
  isResend?: boolean;
  initialBusinessNumber?: string;
  existingProvider?: PaymentIntegrationType;
  /** Refetch the registration status, so the page re-resolves to the pending state. */
  onRegistered: () => Promise<unknown>;
  /** Our view of the account may be out of date -- refetch account and status. */
  onStale: () => Promise<unknown>;
};

export const GrowConnectForm = ({
  isResend = false,
  initialBusinessNumber = '',
  existingProvider,
  onRegistered,
  onStale,
}: GrowConnectFormProps) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const [submitError, setSubmitError] = useState<RegisterError | undefined>(undefined);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        business_number: Yup.string()
          .required(t('growConnect.errors.required'))
          .test('israeli-business-number', t('growConnect.errors.invalidBusinessNumber'), (value) =>
            Boolean(normalizeBusinessNumber(value)),
          ),
        phone: Yup.string()
          .required(t('growConnect.errors.required'))
          .test('israeli-mobile', t('growConnect.errors.invalidPhone'), (value) =>
            Boolean(normalizeIsraeliMobile(value)),
          ),
      }),
    [t],
  );

  const onSubmit = async (values: GrowConnectFormValues, { setFieldError }: FormikHelpers<GrowConnectFormValues>) => {
    setSubmitError(undefined);
    const businessNumber = normalizeBusinessNumber(values.business_number);
    const phone = normalizeIsraeliMobile(values.phone);
    if (!businessNumber || !phone) {
      return;
    }

    try {
      await GrowconnectService.createGrowRegistration({ business_number: businessNumber, phone });
      posthog.capture('Grow registration submitted', { isResend });
      await onRegistered();
    } catch (error) {
      const classified = classifyRegisterError(error, t, {
        isResendForSameBusinessNumber: isResend && businessNumber === initialBusinessNumber,
      });
      posthog.capture('Grow registration failed', { errorType: classified.type, isResend });

      if (classified.type === RegisterErrorType.FIELD && classified.field) {
        setFieldError(classified.field, classified.message);
        return;
      }
      setSubmitError(classified);
      if (classified.type === RegisterErrorType.STALE) {
        await onStale();
      }
    }
  };

  return (
    <Formik
      initialValues={{ business_number: initialBusinessNumber, phone: '' }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}>
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container flexDirection="column" gap={2}>
            {existingProvider && !isResend && (
              <Alert severity="warning">{t('growConnect.form.existingProviderWarning')}</Alert>
            )}

            <TextField
              size="small"
              name="business_number"
              label={t('growConnect.form.businessNumberLabel')}
              value={values.business_number}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isResend}
              error={touched.business_number && Boolean(errors.business_number)}
              helperText={
                (touched.business_number && errors.business_number) || t('growConnect.form.businessNumberHelper')
              }
              inputProps={{ dir: 'ltr', inputMode: 'numeric', autoComplete: 'off', maxLength: 12 }}
            />

            <TextField
              size="small"
              name="phone"
              label={t('growConnect.form.phoneLabel')}
              value={values.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.phone && Boolean(errors.phone)}
              helperText={(touched.phone && errors.phone) || t('growConnect.form.phoneHelper')}
              inputProps={{ dir: 'ltr', inputMode: 'tel', autoComplete: 'off', maxLength: 15 }}
            />

            {submitError && (
              <Alert
                severity={submitError.type === RegisterErrorType.BLOCKING ? 'error' : 'warning'}
                onClose={submitError.type === RegisterErrorType.BLOCKING ? undefined : () => setSubmitError(undefined)}>
                <Typography>{submitError.message}</Typography>
                {submitError.type === RegisterErrorType.BLOCKING && (
                  <Typography fontSize="13px" mt={1}>
                    {t('growConnect.errors.blockingFooter')}
                  </Typography>
                )}
              </Alert>
            )}

            <Grid container justifyContent="center" mt={1}>
              <LoadingButton
                type="submit"
                variant={isResend ? 'outlined' : 'contained'}
                loading={isSubmitting}
                disabled={submitError?.type === RegisterErrorType.BLOCKING}
                sx={{ minWidth: '200px' }}>
                {isResend ? t('growConnect.pending.resendCta') : t('growConnect.form.submitCta')}
              </LoadingButton>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
